import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { Toaster } from "@/components/ui/sonner";
import { PageLoading } from "@/components/page-state";
import { StaleBanner } from "@/components/stale-banner";
import { ApiError, apiGet } from "@/lib/api";
import { startOutboxSync } from "@/lib/sync";
import { TaxonomyProvider } from "@/lib/taxonomy";

import DashboardPage from "@/routes/home";

// Everything past the first screen is split out: the app is used one-handed on
// a phone, so the initial bundle matters more than a warm navigation.
// Login pulls in the Firebase SDK, which is by far the biggest dependency in
// the app and is useless to a signed-in user — worth its own chunk.
const LoginPage = lazy(() => import("@/routes/login"));
const TransactionsPage = lazy(() => import("@/routes/transactions"));
const EarningsPage = lazy(() => import("@/routes/earnings"));
const NewEarningPage = lazy(() => import("@/routes/earning-new"));
const EarningDetailPage = lazy(() => import("@/routes/earning-detail"));
const AnalyticsPage = lazy(() => import("@/routes/analytics"));
const AddPage = lazy(() => import("@/routes/add"));
const CapturePage = lazy(() => import("@/routes/capture"));
const ExpenseDetailPage = lazy(() => import("@/routes/expense-detail"));
const SettingsPage = lazy(() => import("@/routes/settings"));

type AuthState = "checking" | "in" | "out";

// The Next middleware used to bounce unauthenticated page loads to /login.
// This is the same gate, one request, at the root of the app: the API is the
// authority, so a stale or forged cookie still gets nothing.
function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>("checking");
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/login") return;
    let active = true;
    apiGet("/api/auth/me")
      .then(() => active && setState("in"))
      .catch((err: unknown) => {
        if (!active) return;
        setState(err instanceof ApiError && err.isUnauthorized ? "out" : "in");
      });
    return () => {
      active = false;
    };
    // Re-checked per navigation only when it hasn't resolved yet; once signed
    // in, the per-request 401 handling in useApi takes over.
  }, [pathname]);

  return state;
}

export function App() {
  const { pathname } = useLocation();
  const auth = useAuthState();

  // Drain the offline outbox from wherever the user happens to be standing.
  useEffect(() => (auth === "in" ? startOutboxSync() : undefined), [auth]);

  if (pathname !== "/login") {
    if (auth === "checking") return <PageLoading />;
    if (auth === "out") return <Navigate to="/login" replace />;
  }

  return (
    // Scopes and sources are the tenant's own, loaded once and read by every
    // screen — no page renders a list of buckets it inherited from a constant.
    <TaxonomyProvider
      enabled={pathname !== "/login"}
      fallback={<PageLoading />}
    >
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/earnings" element={<EarningsPage />} />
          <Route path="/earnings/new" element={<NewEarningPage />} />
          <Route path="/earnings/:id" element={<EarningDetailPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/add" element={<AddPage />} />
          <Route path="/capture" element={<CapturePage />} />
          <Route path="/expense/:id" element={<ExpenseDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* Unknown paths land on the overview rather than a dead end. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {pathname !== "/login" && <StaleBanner />}
      <Toaster position="top-center" richColors />
    </TaxonomyProvider>
  );
}
